/**
 * 产康单项目预约表单
 * 调试：控制台 `window.__FORM_DEBUG_DISPATCH_MOBILE__ = true` 可打开表单与日历日志（staging/正式包同样可用）。
 * 预约日期 + 开始时段（15 分钟粒度）+ 技师；结束时间由 serviceTime（分钟）推算并在表单项下方展示，提交时仍按此时长计算
 * 预约日为当日时：开始时间须不早于「当前时刻 + 提前分钟数」（见 @/utils/technicianBookingTimeSlots）；可选/禁用状态在时段组件每次渲染时按当时时间计算，非仅在打开弹窗瞬间
 * 作为 WrappedModal 的 baseComponent，form 由 WrappedModal 注入
 */
import React, { useEffect, memo } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Form as AntdForm, Empty, Spin, message, Grid } from 'antd'
import { useWatch } from 'rc-field-form'
import { DatePickerF } from '@/i2antdV5'
import dayjs from 'dayjs'
import {
  appointmentDateToDayStartMs,
  appointmentDateToDayStartSec,
  extractErrorMessage,
  formatExpectedEndTime,
  getTechnicianShiftRestFromRow,
  hasTechnicianSelected,
  isValidServiceMinutes,
  normalizeServiceMinutes,
  parseProjectPlanTechniciansData,
  parseTechnicianDayPlanData,
  parseTechnicianIdForApi,
  renderTextStr
} from '@/utils/renderUtils'
import { getProjectPlanTechnicians, getTechnicianDayPlan } from '@/service/dispatchPage'
import { getDefaultAppointmentStartHm } from '@/utils/technicianBookingTimeSlots'
import {
  getUnassignedDefaultOrPlanStartHm,
  initialAppointmentDate,
  planInitialStartHm
} from '@/utils/unassignedPlanTimeInit'
import {
  getMaternityCenterOrderProfile,
  normalizeMaternityOrderDetailFromApiResult
} from '@/service/orderTypeApis'
import StartTimeSlotSelect from './StartTimeSlotSelect'
import ProjectPlanTechnicianPicker from './ProjectPlanTechnicianPicker'
import MobileBottomSheetDateField from './MobileBottomSheetDateField'

const { useBreakpoint } = Grid

const logDispatchAppointmentForm =
  typeof window !== 'undefined' &&
  (process.env.NODE_ENV === 'development' || window.__FORM_DEBUG_DISPATCH_MOBILE__)
    ? (...args) => {
        console.log('[SingleProjectAppointmentForm]', ...args)
      }
    : undefined

/** 预约日期不可选今日之前；按「天」比较，避免 WebKit 下时分秒与 startOf('day') 比较失误判 */
const disabledDateNotBeforeToday = (current) => {
  if (current == null || (typeof current.isValid === 'function' && !current.isValid())) {
    return false
  }
  return current.isBefore(dayjs(), 'day')
}

/**
 * - useMobileDatePicker===true（且未禁用）：用底部 antd-mobile 日历。
 * - 否则一律 DatePickerF；若 mobileUaDevice===true（常见于新订单嵌入 H5）：面板挂 document.body +
 *   提高 z-index，避免在 Drawer/overflow 内被裁切或点在 web-view 里无响应。
 * Form.Item 会注入 value / onChange，须透传到底层控件。
 */
function AppointmentDateFieldInner ({ useMobileDatePicker, mobileUaDevice, disabledDate, value, onChange, ...rest }) {
  const screens = useBreakpoint()
  const md = screens.md

  let useBottomSheet = false
  if (useMobileDatePicker && mobileUaDevice !== false) {
    if (mobileUaDevice === true) {
      useBottomSheet = md !== true
    } else {
      useBottomSheet = md === false
    }
  }
  if (!useBottomSheet) {
    /** 嵌入底抽屉场景：antd DatePicker 默认挂在触发器附近，易被 overflow 裁剪；挂 body + 大类名由 less 拉高 z-index */
    const embedPcDrawerFix =
      mobileUaDevice === true && !useMobileDatePicker ? {
        getPopupContainer: () => document.body,
        popupClassName: 'dispatch-embed-pc-datepicker-popup'
      } : {}

    return (
      <DatePickerF
        {...rest}
        value={value}
        onChange={onChange}
        dateStrValue
        modalProps={{
          allowClear: false,
          disabledDate,
          style: { width: '100%', maxWidth: 200 },
          ...embedPcDrawerFix
        }}
      />
    )
  }
  return (
    <MobileBottomSheetDateField
      {...rest}
      value={value}
      onChange={onChange}
      disabledDate={disabledDate}
    />
  )
}

const AppointmentDateField = memo(AppointmentDateFieldInner)

/**
 * 必须放在 **antd Form** 子树内：useWatch 依赖 rc-field-form 的 FieldContext；
 * antd-mobile Form 的上下文与其不一致会报警且监听失效。手机端仅用 MobileBottomSheetDateField，不要用 MobileForm 包整单。
 */
const ProjectPlanTechniciansByAppointmentDate = ({ storeId, loadTechniciansRef }) => {
  const appt = useWatch('appointmentDate')
  const dateKey =
    appt == null
      ? null
      : (dayjs.isDayjs(appt)
          ? appt.format('YYYY-MM-DD')
          : String(appt).slice(0, 10))

  useEffect(() => {
    if (!storeId || dateKey == null) return
    loadTechniciansRef.current?.()
  }, [storeId, dateKey, loadTechniciansRef])

  return null
}

/** 班次名仅含 CJK 时与技师名并列（过滤纯英文班次代号等） */
const SHIFT_NAME_SHOW_WITH_TECHNICIAN = /[\u4e00-\u9fff]/

const buildTechnicianTagOptions = (technicianValue) => {
  if (!Array.isArray(technicianValue) || technicianValue.length === 0) return undefined
  const raw = technicianValue
    .map((x) => {
      if (typeof x === 'object' && x != null) {
        return {
          id: String(x.id ?? x.value ?? ''),
          value: renderTextStr(x.name ?? x.value ?? x.label ?? '')
        }
      }
      return { id: String(x), value: String(x) }
    })
    .filter((o) => o.id)
  return raw.length > 0 ? raw : undefined
}

const appointmentStartTimeExtra = (hasValidServiceTime, hasTechnician) => {
  if (!hasValidServiceTime) return '缺少服务时长，请关闭后从列表重新进入'
  if (hasTechnician) return '灰色时段为该技师在所选日期已占用时间（已含服务时长冲突）'
  return '步骤：① 选择预约专属技师 ② 再选择开始时间（未选技师前时段不可点）'
}

/** 预计结束时间展示块（与开始时段联动） */
const ExpectedEndTimeHighlight = ({
  hasValidServiceTime,
  hasTechnician,
  startHm,
  endHm,
  serviceMinutes
}) => {
  if (!hasValidServiceTime) return null
  const ready = !!(startHm && endHm)
  return (
    <div
      className={
        ready
          ? 'appointment-end-highlight appointment-end-highlight--ready'
          : 'appointment-end-highlight appointment-end-highlight--placeholder'
      }
    >
      {ready
        ? (
        <>
          <div className="appointment-end-highlight__head">
            <span className="appointment-end-highlight__badge">预计结束时间</span>
            <span className="appointment-end-highlight__time">{endHm}</span>
          </div>
          <div className="appointment-end-highlight__sub">
            开始 {startHm} · 服务 {serviceMinutes} 分钟
          </div>
        </>
          )
        : (
        <div className="appointment-end-highlight__hint">
          {!hasTechnician
            ? (
            <>
              请先选择<strong>预约专属技师</strong>，再选择开始时间；之后将显示预计结束时间
            </>
              )
            : (
            <>
              在上方选择开始时段后，将在此处显示<strong>预计结束时间</strong>
            </>
              )}
        </div>
          )}
    </div>
  )
}

ExpectedEndTimeHighlight.propTypes = {
  hasValidServiceTime: PropTypes.bool,
  hasTechnician: PropTypes.bool,
  startHm: PropTypes.string,
  endHm: PropTypes.string,
  serviceMinutes: PropTypes.number
}

// ─── 表单主体 ────────────────────────────────────────────────

class SingleProjectAppointmentForm extends React.Component {
  static propTypes = {
    storeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /** 月子中心订单 id：有则请求详情，用接口返回的 technicians 作为「预约专属技师」标签选项 */
    orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /** 派单页 Redux 选中订单（modal 未传到 orderId 时的兜底） */
    dispatchSelectedOrderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /** 服务时长（分钟），须为套餐项目行 record.serviceTime，用于禁选与预计结束时间 */
    serviceTime: PropTypes.number,
    form: PropTypes.object,
    technicianValue: PropTypes.array,
    /**
     * 未分配产康安排（新订单等）：有值时与 UnassignedProjectPlanBookPanel 行为一致
     *（时段默认、改日/选产康师规则、不强制当日提前量/重叠禁选，提交由父级处理）
     */
    unassignedPlan: PropTypes.object,
    /**
     * 新订单·手机 H5 / 微信 web-view：预约日期用更适合触控的 DatePicker 表现（大高度、只读防键盘、下拉面挂 body）
     */
    useMobileDatePicker: PropTypes.bool,
    /** undefined：不按 UA 限制；true/false：来自 getParsedUserAgent().isMobileDevice（新订单手机页） */
    mobileUaDevice: PropTypes.bool
  }

  state = {
    bookedRanges: [],
    fetchingPlan: false,
    planTechnicians: [],
    fetchingPlanTechnicians: false,
    /** 来自 getMaternityCenterOrderProfile 的 technicians；undefined 表示未拉取或未传 orderId，标签用 technicianValue */
    orderProfileTechnicians: undefined
  }

  loadProjectPlanTechniciansRef = React.createRef()

  applyUnassignedInitialFromPlan = () => {
    const p = this.props.unassignedPlan
    const f = this.props.form?.current
    if (!p || !f?.setFieldsValue) return
    const nextDate = initialAppointmentDate(p)
    const nextStart = getUnassignedDefaultOrPlanStartHm(p, this.props.serviceTime)
    f.setFieldsValue({
      appointmentDate: nextDate,
      appointmentStartTime: nextStart,
      technicianId: undefined
    })
  }

  componentDidMount () {
    this.syncOrderProfileTechnicians()
    if (this.props.unassignedPlan) {
      this.applyUnassignedInitialFromPlan()
    }
    requestAnimationFrame(() => {
      const f = this.props.form?.current
      if (!f?.getFieldsValue) return
      const { technicianId, appointmentDate } = f.getFieldsValue(true)
      this.loadBookedSlots(technicianId, appointmentDate)
    })
  }

  componentDidUpdate (prevProps) {
    if (prevProps.storeId !== this.props.storeId) {
      const f = this.props.form?.current
      f?.setFieldsValue?.({
        technicianId: undefined,
        appointmentStartTime: undefined
      })
      this.setState({ bookedRanges: [] })
      requestAnimationFrame(() => {
        if (!f?.getFieldsValue) return
        const { technicianId, appointmentDate } = f.getFieldsValue(true) || {}
        this.loadBookedSlots(technicianId, appointmentDate)
      })
    }
    if (String(this.resolveProfileOrderId(prevProps) ?? '') !== String(this.resolveProfileOrderId() ?? '')) {
      this.syncOrderProfileTechnicians()
    }
    if (
      this.props.unassignedPlan &&
      String(this.props.unassignedPlan?.id) !== String(prevProps.unassignedPlan?.id)
    ) {
      this.applyUnassignedInitialFromPlan()
      requestAnimationFrame(() => {
        const f = this.props.form?.current
        if (!f?.getFieldsValue) return
        const { technicianId, appointmentDate } = f.getFieldsValue(true) || {}
        this.loadBookedSlots(technicianId, appointmentDate)
      })
    }
  }

  /** 预约弹窗用订单 id：优先 props.orderId，否则派单页选中的客人订单 */
  resolveProfileOrderId = (props = this.props) => {
    const { orderId, dispatchSelectedOrderId } = props
    if (orderId != null && orderId !== '') return orderId
    if (dispatchSelectedOrderId != null && dispatchSelectedOrderId !== '') return dispatchSelectedOrderId
    return null
  }

  syncOrderProfileTechnicians = async () => {
    const profileOrderId = this.resolveProfileOrderId()
    if (profileOrderId == null || profileOrderId === '') {
      this.setState({ orderProfileTechnicians: undefined })
      return
    }
    const requestedId = profileOrderId
    try {
      const res = await getMaternityCenterOrderProfile(requestedId)
      if (String(this.resolveProfileOrderId() ?? '') !== String(requestedId ?? '')) return
      const detail = normalizeMaternityOrderDetailFromApiResult(res)
      const tech = detail.technicians
      this.setState({
        orderProfileTechnicians: Array.isArray(tech) ? tech : []
      })
    } catch (e) {
      if (String(this.resolveProfileOrderId() ?? '') !== String(requestedId ?? '')) return
      message.error(extractErrorMessage(e, '获取订单客户信息失败'))
      this.setState({ orderProfileTechnicians: undefined })
    }
  }

  loadProjectPlanTechnicians = async () => {
    const { storeId, form } = this.props
    if (!storeId) {
      this.setState({ planTechnicians: [] })
      return
    }
    const appt = form?.current?.getFieldValue('appointmentDate')
    this.setState({ fetchingPlanTechnicians: true })
    try {
      const res = await getProjectPlanTechnicians({
        storeId,
        date: appointmentDateToDayStartSec(appt != null ? appt : dayjs())
      })
      const next = parseProjectPlanTechniciansData(res)
      this.setState({ planTechnicians: next, fetchingPlanTechnicians: false }, () => {
        const tid = form?.current?.getFieldValue('technicianId')
        if (tid != null && tid !== '' && !next.some((t) => String(t.technicianId) === String(tid))) {
          form?.current?.setFieldsValue({ technicianId: undefined, appointmentStartTime: undefined })
          const { technicianId, appointmentDate: ad } = form?.current?.getFieldsValue(true) || {}
          this.loadBookedSlots(technicianId, ad)
        }
      })
    } catch (e) {
      message.error(extractErrorMessage(e, '获取当班技师列表失败'))
      this.setState({ planTechnicians: [], fetchingPlanTechnicians: false })
    }
  }

  loadBookedSlots = async (technicianId, appointmentDate) => {
    const tid = parseTechnicianIdForApi(technicianId)
    const dateMs = appointmentDateToDayStartMs(appointmentDate)
    if (tid == null) {
      this.setState({ bookedRanges: [], fetchingPlan: false })
      return
    }
    this.setState({ fetchingPlan: true })
    try {
      const res = await getTechnicianDayPlan({ technicianId: tid, date: dateMs })
      this.setState({ bookedRanges: parseTechnicianDayPlanData(res), fetchingPlan: false })
    } catch (e) {
      this.setState({ bookedRanges: [], fetchingPlan: false })
      message.error(extractErrorMessage(e, '获取技师占用时段失败'))
    }
  }

  render () {
    const { form, technicianValue, serviceTime, unassignedPlan, useMobileDatePicker, mobileUaDevice } = this.props
    const serviceMinutes = normalizeServiceMinutes(serviceTime)
    const hasValidServiceTime = isValidServiceMinutes(serviceMinutes)
    const isUnassigned = !!unassignedPlan
    const technicianItemLabel = isUnassigned ? '产康师' : '预约专属技师'
    const startTimeItemLabel = isUnassigned ? '开始时间' : '预约开始时间'

    const { planTechnicians, fetchingPlanTechnicians } = this.state
    const staffOptions = (planTechnicians || []).map((s) => {
      const shiftStr = s.shiftName ? renderTextStr(s.shiftName) : ''
      const shiftPart = shiftStr && SHIFT_NAME_SHOW_WITH_TECHNICIAN.test(shiftStr) ? shiftStr : ''
      return {
        id: String(s.technicianId),
        value: [renderTextStr(s.technicianName), shiftPart].filter(Boolean).join(' · ') || `技师${s.technicianId}`
      }
    })
    const profileOrderId = this.resolveProfileOrderId()
    const tagSourceTechnicians =
      profileOrderId != null &&
      profileOrderId !== '' &&
      this.state.orderProfileTechnicians !== undefined
        ? this.state.orderProfileTechnicians
        : technicianValue
    const technicianTagOptions = buildTechnicianTagOptions(tagSourceTechnicians)

    this.loadProjectPlanTechniciansRef.current = this.loadProjectPlanTechnicians

    const handleValuesChange = (changedValues, allValues) => {
      logDispatchAppointmentForm?.('onValuesChange', { changedValues, allValues })
      if (isUnassigned && unassignedPlan) {
        const p = unassignedPlan
        if ('appointmentDate' in changedValues) {
          const { technicianId, appointmentDate } = allValues || {}
          const d = appointmentDate != null ? dayjs(appointmentDate) : null
          const planDay = initialAppointmentDate(p)
          const onPlanDay = d?.isValid() && d.isSame(planDay, 'day')
          const fromPlan = onPlanDay ? planInitialStartHm(p) : undefined
          form?.current?.setFieldValue(
            'appointmentStartTime',
            fromPlan ||
              getDefaultAppointmentStartHm(appointmentDate, hasValidServiceTime ? serviceMinutes : 60)
          )
          this.loadProjectPlanTechnicians()
          const { technicianId: tid, appointmentDate: ad } = allValues || {}
          this.loadBookedSlots(tid, ad)
        } else if ('technicianId' in changedValues) {
          const { technicianId, appointmentDate } = allValues || {}
          this.loadBookedSlots(technicianId, appointmentDate)
        }
        return
      }
      if ('appointmentDate' in changedValues || 'technicianId' in changedValues) {
        form?.current?.setFieldValue?.('appointmentStartTime', undefined)
        const { technicianId, appointmentDate } = allValues || {}
        this.loadBookedSlots(technicianId, appointmentDate)
      }
    }

    const { bookedRanges, fetchingPlan } = this.state

    /* 外层固定 antd Form，保证 useWatch / validateFields / 与 WrappedModal ref 一致；手机日历仅作控件 */
    const FormShell = AntdForm
    const FormItem = AntdForm.Item

    return (
      <div
        className={
          useMobileDatePicker
            ? 'single-project-appointment-form single-project-appointment-form--mobile-date'
            : 'single-project-appointment-form'
        }
      >
        <FormShell ref={form} layout="vertical" onValuesChange={handleValuesChange}>
          <ProjectPlanTechniciansByAppointmentDate
            storeId={this.props.storeId}
            loadTechniciansRef={this.loadProjectPlanTechniciansRef}
          />
          <FormItem
            name="appointmentDate"
            label="预约日期"
            rules={[{ required: true, message: '请选择预约日期' }]}
            initialValue={isUnassigned ? undefined : dayjs()}
          >
            <AppointmentDateField
              useMobileDatePicker={useMobileDatePicker}
              mobileUaDevice={mobileUaDevice}
              disabledDate={disabledDateNotBeforeToday}
            />
          </FormItem>
          <FormItem
            name="technicianId"
            label={technicianItemLabel}
            rules={[{ required: true, message: isUnassigned ? '请选择产康师' : '请选择技师' }]}
          >
            {fetchingPlanTechnicians && staffOptions.length === 0
              ? (
              <Spin><div style={{ minHeight: 40 }} /></Spin>
                )
              : staffOptions.length === 0
                ? (
                <Empty
                  description={isUnassigned ? '暂无当班技师' : '暂无当班技师，请换日期或联系门店'}
                />
                  )
                : (
                <ProjectPlanTechnicianPicker
                  storeScopeKey={this.props.storeId}
                  options={staffOptions}
                  tagOptions={technicianTagOptions}
                  loading={!!fetchingPlanTechnicians}
                  useMobilePicker={!!useMobileDatePicker}
                />
                  )}
          </FormItem>
          <FormItem
            noStyle
            shouldUpdate={(prev, curr) =>
              prev.appointmentDate !== curr.appointmentDate ||
              prev.technicianId !== curr.technicianId ||
              prev.appointmentStartTime !== curr.appointmentStartTime
            }
          >
            {({ getFieldValue }) => {
              const startHm = getFieldValue('appointmentStartTime')
              const technicianIdVal = getFieldValue('technicianId')
              const hasTechnician = hasTechnicianSelected(technicianIdVal)
              const appointmentDateField = getFieldValue('appointmentDate')
              const techRow = (planTechnicians || []).find(
                (t) => String(t.technicianId) === String(technicianIdVal)
              )
              const { shiftStartMinutes, shiftEndMinutes, restStartMinutes, restEndMinutes } =
                getTechnicianShiftRestFromRow(techRow)
              const endHm = hasValidServiceTime
                ? formatExpectedEndTime(appointmentDateField, startHm, serviceMinutes)
                : null
              const unassignedStartExtra = isUnassigned
                ? (hasValidServiceTime
                    ? (hasTechnician
                        ? '灰格为已过去、早于上班、或整段服务超出下班等；橙/红为已有工作/重叠，未强禁重叠时可提交以服务端为准'
                        : '先选择产康师，再点选开始时间')
                    : '无法根据当前安排推算服务时长，请从列表重选')
                : null
              return (
                <>
                  <Spin spinning={fetchingPlan}>
                    <FormItem
                      name="appointmentStartTime"
                      label={startTimeItemLabel}
                      rules={[{ required: true, message: '请选择开始时间' }]}
                      extra={isUnassigned
                        ? unassignedStartExtra
                        : appointmentStartTimeExtra(hasValidServiceTime, hasTechnician)}
                    >
                      <StartTimeSlotSelect
                        disabledDate={appointmentDateField || dayjs()}
                        serviceTimeMinutes={hasValidServiceTime ? serviceMinutes : 0}
                        bookedRanges={bookedRanges}
                        shiftStartMinutes={shiftStartMinutes}
                        shiftEndMinutes={shiftEndMinutes}
                        restStartMinutes={restStartMinutes}
                        restEndMinutes={restEndMinutes}
                        disableUntilTechnicianSelected={!hasTechnician}
                        applyBookingLeadConstraint={!isUnassigned}
                        disableSlotsOverlappingBooked={!isUnassigned}
                      />
                    </FormItem>
                  </Spin>
                  <ExpectedEndTimeHighlight
                    hasValidServiceTime={hasValidServiceTime}
                    hasTechnician={hasTechnician}
                    startHm={startHm}
                    endHm={endHm}
                    serviceMinutes={serviceMinutes}
                  />
                </>
              )
            }}
          </FormItem>
        </FormShell>
      </div>
    )
  }
}

const mapStateToProps = (state) => {
  const dispatchSelectedOrderId = state.order?.dispatchPage?.selectedOrderId ?? null
  return { dispatchSelectedOrderId }
}

export { ExpectedEndTimeHighlight, buildTechnicianTagOptions, ProjectPlanTechnicianPicker }
export { getDefaultAppointmentStartHm } from '@/utils/technicianBookingTimeSlots'

export default connect(mapStateToProps, null, null, { forwardRef: true })(SingleProjectAppointmentForm)
